import { useTranslation } from "@refinedev/core";
import { Label } from "components/label";
import { SwitchButton } from "components/switchButton";
import { IAdditionalSetupData, ISimpleEventDetail, UserType } from "interfaces";
import { get } from "lodash";
import { IS_SHOW_BIB_LIST } from "utills/constants";
import { canAccessByType } from "utills/helpers";

export const BasicInformation = ({
  eventDetailData,
  additionalSetupData,
}: {
  eventDetailData: ISimpleEventDetail;
  additionalSetupData: IAdditionalSetupData | null;
}) => {
  const { translate } = useTranslation();
  return (
    <div className="box-container p-4 sm:p-6">
      <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 text-sm lg:gap-x-6">
        <div className="col-span-full w-fit">
          <div className="flex flex-row items-center gap-x-4">
            <SwitchButton
              label={translate(
                "eventSetup.additionalSetup.basicInformation.displayParticipantsList"
              )}
              hint={translate(
                "eventSetup.additionalSetup.basicInformation.displayParticipantsListHint"
              )}
              checked={additionalSetupData?.is_have_participant_list ?? false}
              onChange={() => {}}
              disabled
            />
            {additionalSetupData?.is_have_participant_list ? (
              <div className="font-medium">
                {
                  IS_SHOW_BIB_LIST.find(
                    (item) => item.value === additionalSetupData.show_bib
                  )?.label
                }
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
        <div className="col-span-full w-fit">
          <SwitchButton
            label={translate(
              "eventSetup.additionalSetup.basicInformation.hideVAT"
            )}
            hint={translate(
              "eventSetup.additionalSetup.basicInformation.hideVATHint"
            )}
            checked={additionalSetupData?.is_hide_vat ?? false}
            onChange={() => {}}
            disabled
          />
        </div>
        <div className="col-span-full w-fit">
          <SwitchButton
            label={translate(
              "eventSetup.additionalSetup.basicInformation.displayGroupButton"
            )}
            hint={translate(
              "eventSetup.additionalSetup.basicInformation.displayGroupButtonHint"
            )}
            checked={additionalSetupData?.display_group_button ?? false}
            onChange={() => {}}
            disabled
          />
        </div>
        <div className="col-span-full w-fit">
          <SwitchButton
            hint={translate(
              "eventSetup.additionalSetup.basicInformation.displayOnTheWhitelabelHint"
            )}
            label={translate(
              "eventSetup.additionalSetup.basicInformation.displayOnTheWhitelabel"
            )}
            checked={
              additionalSetupData?.is_display_on_the_organization_detail_page ??
              false
            }
            onChange={() => {}}
            disabled
          />
        </div>
        {canAccessByType(UserType.ADMIN) && (
          <div className="col-span-full w-fit">
            <SwitchButton
              label={translate(
                "eventSetup.additionalSetup.basicInformation.usePassTool"
              )}
              checked={additionalSetupData?.use_pass_tool ?? false}
              onChange={() => {}}
              disabled
            />
          </div>
        )}
        {canAccessByType(UserType.ADMIN) && (
          <>
          <div className="col-span-full">
            <SwitchButton
              label="Is Static HTML"
              checked={additionalSetupData?.is_static_html ?? false}
              onChange={() => {}}
              disabled
            />
          </div>
          {(additionalSetupData?.is_static_html && additionalSetupData?.is_static_html) && (
            <div dangerouslySetInnerHTML={{__html: additionalSetupData.static_html_content || ""}} />
          )}
          </>
        )}

        <div className="col-span-full">
          <Label
            id="basic_information.event_term_url"
            name={translate(
              "eventSetup.additionalSetup.basicInformation.eventTClink"
            )}
            hint={translate(
              "eventSetup.additionalSetup.basicInformation.eventTClinkHint"
            )}
            required
          />
          <div className="flex flex-col gap-y-4">
            {eventDetailData.languages.map((lang) => (
              <div key={lang}>
                <div className="relative flex items-center gap-x-2">
                  <img
                    src={`/images/flags/circle-${lang}.png`}
                    alt="Language Icon"
                    className="size-5"
                  />
                  {get(additionalSetupData?.event_term_url, lang, "")}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="sm:col-span-3">
          <Label
            id="basic_information.hold_ticket_time"
            name={translate(
              "eventSetup.additionalSetup.basicInformation.holdTicketTime"
            )}
            hint={translate(
              "eventSetup.additionalSetup.basicInformation.holdTicketTimeHint"
            )}
            required
          />
          {additionalSetupData?.hold_ticket_time
            ? Math.floor(additionalSetupData?.hold_ticket_time / 60)
            : ""}
        </div>
        <div className="sm:col-span-3">
          <Label
            id="basic_information.max_tickets_to_buy"
            name={translate(
              "eventSetup.additionalSetup.basicInformation.maxTicketsQuantityPerAccount"
            )}
            hint={translate(
              "eventSetup.additionalSetup.basicInformation.maxTicketsQuantityPerAccountHint"
            )}
          />
          {additionalSetupData?.max_tickets_to_buy || (
            <span className="text-gray-400">N/A</span>
          )}
        </div>
        <div className="sm:col-span-3">
          <Label
            id="basic_information.order_min_qty_ticket"
            name={translate(
              "eventSetup.additionalSetup.basicInformation.minTicketsQuantityPerOrder"
            )}
            required
          />
          {additionalSetupData?.order_min_qty_ticket}
        </div>
        <div className="sm:col-span-3">
          <Label
            id="basic_information.order_max_qty_ticket"
            name={translate(
              "eventSetup.additionalSetup.basicInformation.maxTicketsQuantityPerOrder"
            )}
            required
          />
          {additionalSetupData?.order_max_qty_ticket}
        </div>

        <div className="col-span-full">
          <Label
            id="basic_information.note_individual_ticket_purchase_flow"
            name={translate(
              "eventSetup.additionalSetup.basicInformation.noteInTheIndividualFlow"
            )}
            hint={translate(
              "eventSetup.additionalSetup.basicInformation.noteInTheIndividualFlowHint"
            )}
          />
          <div className="flex flex-col gap-y-4">
            {eventDetailData.languages.map((lang) => (
              <div key={lang}>
                <div className="relative flex items-center gap-x-2">
                  <img
                    src={`/images/flags/circle-${lang}.png`}
                    alt="Language Icon"
                    className="size-5"
                  />
                  {get(
                    additionalSetupData?.note_individual_ticket_purchase_flow,
                    lang,
                    ""
                  ) ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: `${get(
                          additionalSetupData?.note_individual_ticket_purchase_flow,
                          lang,
                          ""
                        )}`,
                      }}
                    />
                  ) : (
                    <span className="text-gray-400">N/A</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="col-span-full">
          <Label
            id="basic_information.note_group_ticket_purchase_flow"
            name={translate(
              "eventSetup.additionalSetup.basicInformation.noteInTheGroupFlow"
            )}
            hint={translate(
              "eventSetup.additionalSetup.basicInformation.noteInTheGroupFlowHint"
            )}
          />
          <div className="flex flex-col gap-y-4">
            {eventDetailData.languages.map((lang) => (
              <div key={lang}>
                <div className="relative flex items-center gap-x-2">
                  <img
                    src={`/images/flags/circle-${lang}.png`}
                    alt="Language Icon"
                    className="size-5"
                  />
                  {get(
                    additionalSetupData?.note_group_ticket_purchase_flow,
                    lang,
                    ""
                  ) ? (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: `${get(
                          additionalSetupData?.note_group_ticket_purchase_flow,
                          lang,
                          ""
                        )}`,
                      }}
                    />
                  ) : (
                    <span className="text-gray-400">N/A</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
